/**
 * Phase 4.3: Workflow Automation - Notification Context
 * Provides real-time notifications and alert management
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, AlertTriangle, Clock, DollarSign, Package2 } from 'lucide-react';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Notification types and priorities
export const NOTIFICATION_TYPES = {
  CRITICAL_STOCK: 'critical_stock',
  LOW_MARGIN: 'low_margin', 
  INACTIVE_SKU: 'inactive_sku',
  INCOMPLETE_DATA: 'incomplete_data',
  SYSTEM: 'system'
};

export const NOTIFICATION_PRIORITIES = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Backend API configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;
const TENANT_ID = 'tenant-123';

export const NotificationProvider = ({ children }) => {
  // State Management
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [alertRules, setAlertRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastCheck, setLastCheck] = useState(new Date());

  // Default alert rules (Phase 4.3 specifications)
  const defaultAlertRules = [
    {
      id: 'stock_below_reorder',
      name: 'Stock Below Reorder Point',
      type: NOTIFICATION_TYPES.CRITICAL_STOCK,
      condition: (product) => (product.on_hand || 0) < (product.reorder_point || 10),
      priority: NOTIFICATION_PRIORITIES.CRITICAL,
      enabled: true,
      icon: Package2,
      message: (product) => `Critical Stock: ${product.sku} has ${product.on_hand || 0} units (Reorder: ${product.reorder_point || 10})`
    },
    {
      id: 'margin_below_threshold',
      name: 'Margin Below 15%',
      type: NOTIFICATION_TYPES.LOW_MARGIN,
      condition: (product) => {
        const margin = calculateMargin(product);
        return margin < 15 && margin > 0;
      },
      priority: NOTIFICATION_PRIORITIES.HIGH,
      enabled: true,
      icon: DollarSign,
      message: (product) => `Low Margin: ${product.sku} margin is ${calculateMargin(product).toFixed(1)}% (Target: ≥15%)`
    },
    {
      id: 'inactive_sku_30_days',
      name: 'SKU Inactive > 30 Days',
      type: NOTIFICATION_TYPES.INACTIVE_SKU,
      condition: (product) => {
        // Mock inactive logic - in production would check last sale date
        const mockDaysSinceLastSale = Math.floor(Math.random() * 120);
        return mockDaysSinceLastSale > 30;
      },
      priority: NOTIFICATION_PRIORITIES.MEDIUM,
      enabled: true,
      icon: Clock,
      message: (product) => `Inactive SKU: ${product.sku} hasn't sold in 30+ days`
    },
    {
      id: 'incomplete_pricing_data',
      name: 'Missing Cost or Pricing Data',
      type: NOTIFICATION_TYPES.INCOMPLETE_DATA,
      condition: (product) => !product.effective_cost || !product.price || product.effective_cost === 0 || product.price === 0,
      priority: NOTIFICATION_PRIORITIES.HIGH,
      enabled: true,
      icon: AlertTriangle,
      message: (product) => `Incomplete Data: ${product.sku} missing ${!product.effective_cost ? 'cost' : 'pricing'} information`
    }
  ];

  // Initialize alert rules
  useEffect(() => {
    setAlertRules(defaultAlertRules);
  }, []);

  // Calculate margin helper function
  const calculateMargin = useCallback((product) => {
    const price = product.price || 0;
    const cost = product.effective_cost || 0;
    if (price === 0) return 0;
    return ((price - cost) / price) * 100;
  }, []);

  // Check for new alerts based on rules
  const checkAlerts = useCallback(async (products) => {
    if (!products || products.length === 0) return;

    const newNotifications = [];
    const currentTime = new Date();

    products.forEach(product => {
      alertRules.forEach(rule => {
        if (!rule.enabled) return;

        try {
          if (rule.condition(product)) {
            // Check if we already have a recent notification for this product/rule
            const existingNotification = notifications.find(n => 
              n.productId === product.id && 
              n.ruleId === rule.id && 
              (currentTime - new Date(n.created)) < 24 * 60 * 60 * 1000 // 24 hours
            );

            if (!existingNotification) {
              const notification = {
                id: `${rule.id}_${product.id}_${Date.now()}`,
                productId: product.id,
                ruleId: rule.id,
                type: rule.type,
                priority: rule.priority,
                title: rule.name,
                message: rule.message(product),
                product: {
                  sku: product.sku,
                  name: product.name,
                  category: product.category
                },
                icon: rule.icon,
                created: currentTime.toISOString(),
                read: false,
                dismissed: false
              };
              newNotifications.push(notification);
            }
          }
        } catch (error) {
          console.error(`Error checking rule ${rule.id} for product ${product.sku}:`, error);
        }
      });
    });

    if (newNotifications.length > 0) {
      setNotifications(prev => [...newNotifications, ...prev]);
      setUnreadCount(prev => prev + newNotifications.length);
      
      // Show toast for critical alerts - DISABLED FOR DEVELOPMENT
      // Uncomment below to enable toast notifications in production
      
      /*
      newNotifications.forEach(notification => {
        if (notification.priority === NOTIFICATION_PRIORITIES.CRITICAL) {
          toast.error(notification.message, {
            description: notification.product.name,
            icon: <notification.icon className="w-4 h-4" />,
            duration: 5000
          });
        } else if (notification.priority === NOTIFICATION_PRIORITIES.HIGH) {
          toast.warning(notification.message, {
            description: notification.product.name,
            icon: <notification.icon className="w-4 h-4" />,
            duration: 3000
          });
        }
      });
      */

      console.log(`NotificationSystem: Generated ${newNotifications.length} new alerts (toast disabled for dev)`);
    }

    setLastCheck(currentTime);
  }, [alertRules, notifications]);

  // Fetch product data and check alerts
  const refreshAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/product-master/list?tenant_id=${TENANT_ID}&page_size=100`);
      const products = response.data.items || [];
      
      await checkAlerts(products);
      
      console.log(`NotificationSystem: Checked ${products.length} products for alerts`);
    } catch (error) {
      console.error('Error refreshing alerts:', error);
      toast.error('Failed to refresh alerts');
    } finally {
      setLoading(false);
    }
  }, [checkAlerts]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(n => ({ ...n, read: true }))
    );
    setUnreadCount(0);
  }, []);

  // Dismiss notification
  const dismissNotification = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => 
        n.id === notificationId ? { ...n, dismissed: true } : n
      )
    );
    if (!notifications.find(n => n.id === notificationId)?.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  }, [notifications]);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Update alert rule
  const updateAlertRule = useCallback((ruleId, updates) => {
    setAlertRules(prev => 
      prev.map(rule => 
        rule.id === ruleId ? { ...rule, ...updates } : rule
      )
    );
  }, []);

  // Get filtered notifications
  const getNotifications = useCallback((options = {}) => {
    const { unreadOnly = false, type = null, priority = null, limit = null } = options;
    
    let filtered = notifications.filter(n => !n.dismissed);
    
    if (unreadOnly) {
      filtered = filtered.filter(n => !n.read);
    }
    
    if (type) {
      filtered = filtered.filter(n => n.type === type);
    }
    
    if (priority) {
      filtered = filtered.filter(n => n.priority === priority);
    }
    
    // Sort by priority and creation time
    filtered.sort((a, b) => {
      const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      const aPriority = priorityOrder[a.priority] || 0;
      const bPriority = priorityOrder[b.priority] || 0;
      
      if (aPriority !== bPriority) {
        return bPriority - aPriority;
      }
      
      return new Date(b.created) - new Date(a.created);
    });
    
    if (limit) {
      filtered = filtered.slice(0, limit);
    }
    
    return filtered;
  }, [notifications]);

  // Auto-refresh alerts every 5 minutes - DISABLED FOR DEVELOPMENT
  useEffect(() => {
    // Disable auto-refresh during development to prevent notification spam
    // Uncomment below to enable in production
    
    /*
    const interval = setInterval(refreshAlerts, 5 * 60 * 1000);
    
    // Initial refresh
    refreshAlerts();
    
    return () => clearInterval(interval);
    */
  }, [refreshAlerts]);

  const contextValue = {
    notifications,
    unreadCount,
    alertRules,
    loading,
    lastCheck,
    
    // Methods
    refreshAlerts,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    clearAllNotifications,
    updateAlertRule,
    getNotifications,
    
    // Constants
    NOTIFICATION_TYPES,
    NOTIFICATION_PRIORITIES
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;